import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ORDER_STATUS_LABELS_RU,
  type BankAllocationItem,
  type BankCandidateOrderDto,
  type BankLineDto,
  type OrderStatus,
} from '@crm/shared'
import { allocateBankLine, getBankLineCandidates } from '../../shared/api/bank.api'
import { apiErrorText } from '../../shared/api/errorText'
import { PageLoader } from '../../shared/ui/Spinner'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { toast } from '../../shared/ui/toastStore'
import { MoneyInput } from '../orders/components/MoneyInput'
import { BankClientPicker } from './BankClientPicker'
import { BankMatchBadge, formatBankDate, formatBankMoney } from './bankFormat'

const round2 = (v: number): number => Math.round(v * 100) / 100

type Row = { amount: number | null; invoiceId: string | null; linkPaymentId: string | null }

/** Строка заказа по умолчанию: счёт — единственный счёт заказа, сумма пустая. */
function emptyRow(order: BankCandidateOrderDto): Row {
  return {
    amount: null,
    invoiceId: order.invoices.length === 1 ? (order.invoices[0]?.id ?? null) : null,
    linkPaymentId: null,
  }
}

/**
 * Окно разноса банковского поступления (этап 3, Б3): клиент, его заказы к оплате
 * и сколько из платежа идёт в какой заказ. Предложение программы уже подставлено —
 * бухгалтер подтверждает его или делит сумму сам. Неразнесённый остаток остаётся
 * в очереди.
 */
export function BankAllocateModal({
  line,
  onClose,
  onDone,
}: {
  line: BankLineDto
  onClose: () => void
  onDone: () => void
}) {
  const [pickedClientId, setPickedClientId] = useState<string | undefined>(undefined)
  const [rows, setRows] = useState<Record<string, Row>>({})
  const [saveInn, setSaveInn] = useState(true)
  const [saveNumber, setSaveNumber] = useState(true)
  const [saving, setSaving] = useState(false)
  // Поле суммы само хранит набранный текст — после подстановки предложения пересоздаём его.
  const [prefill, setPrefill] = useState(0)

  const query = useQuery({
    queryKey: ['bank-line-candidates', line.id, pickedClientId ?? ''],
    queryFn: () => getBankLineCandidates(line.id, pickedClientId),
  })
  const data = query.data

  // Подставляем предложение программы каждый раз, когда пришли данные по клиенту.
  useEffect(() => {
    if (!data) return
    const next: Record<string, Row> = {}
    for (const order of data.orders) {
      const p = data.proposal.find((x) => x.orderId === order.orderId)
      next[order.orderId] = p
        ? {
            amount: p.amount,
            invoiceId: p.invoiceId ?? null,
            linkPaymentId: p.linkPaymentId ?? null,
          }
        : emptyRow(order)
    }
    setRows(next)
    setPrefill((v) => v + 1)
  }, [data])

  const remaining = data?.line.remaining ?? line.remaining
  const total = useMemo(
    () => round2(Object.values(rows).reduce((s, r) => s + (r.amount ?? 0), 0)),
    [rows],
  )
  const rest = round2(remaining - total)
  const client = data?.clients.find((c) => c.id === data.clientId) ?? null
  const hint = data?.invoiceNumberHint ?? null
  const hintUsed =
    !!hint &&
    (rows[Object.keys(rows).find((id) => rows[id]?.invoiceId === hint.invoiceId) ?? '']?.amount ??
      0) > 0
  const innToSave = !!client && !client.inn?.trim() && !!line.counterpartyInn

  const setRow = (orderId: string, patch: Partial<Row>) =>
    setRows((prev) => ({ ...prev, [orderId]: { ...prev[orderId]!, ...patch } }))

  /**
   * Сколько можно положить в заказ одним нажатием: весь ещё не распределённый
   * остаток платежа (вместе с тем, что уже стоит в этой строке), но не больше
   * остатка по заказу.
   */
  const fillAmount = (order: BankCandidateOrderDto): number =>
    Math.max(0, round2(Math.min(rest + (rows[order.orderId]?.amount ?? 0), order.remaining)))

  const fill = (order: BankCandidateOrderDto) => {
    const amount = fillAmount(order)
    if (amount <= 0) return
    setRow(order.orderId, { amount, linkPaymentId: null })
    setPrefill((v) => v + 1)
  }
  // Заказ один — сумму платежа в шапке можно нажать, и она встанет в этот заказ.
  const onlyOrder = data?.orders.length === 1 ? (data.orders[0] ?? null) : null

  const submit = async () => {
    if (!data?.clientId) return
    const items: BankAllocationItem[] = data.orders
      .filter((o) => (rows[o.orderId]?.amount ?? 0) > 0)
      .map((o) => {
        const r = rows[o.orderId]!
        return {
          orderId: o.orderId,
          invoiceId: r.invoiceId,
          amount: r.amount ?? 0,
          ...(r.linkPaymentId ? { linkPaymentId: r.linkPaymentId } : {}),
        }
      })
    setSaving(true)
    try {
      await allocateBankLine(line.id, {
        clientId: data.clientId,
        items,
        ...(innToSave && saveInn ? { saveInn: true } : {}),
        ...(hint && hintUsed && saveNumber ? { invoiceNumbers: [hint] } : {}),
      })
      toast.success(
        rest > 0.005
          ? `Разнесено ${formatBankMoney(total)}; ${formatBankMoney(rest)} осталось в очереди`
          : 'Платёж разнесён по заказам',
      )
      onDone()
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось разнести платёж'))
    } finally {
      setSaving(false)
    }
  }

  const canSubmit = !!data?.clientId && total > 0 && rest >= -0.005 && !saving

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose()
      }}
    >
      <div
        role="dialog"
        aria-label="Разнести платёж"
        className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl border border-gray-200 bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-800">Разнести платёж</h3>
          <button
            onClick={onClose}
            disabled={saving}
            aria-label="Закрыть"
            className="text-lg leading-none text-gray-400 hover:text-gray-600 disabled:opacity-40"
          >
            ×
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto p-4">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              {onlyOrder && fillAmount(onlyOrder) > 0 ? (
                <button
                  type="button"
                  onClick={() => fill(onlyOrder)}
                  title={`Разнести всю сумму в заказ ${onlyOrder.requestNumber ?? ''}`}
                  className="text-lg font-semibold tabular-nums text-brand-600 underline decoration-dotted underline-offset-4 hover:text-brand-700"
                >
                  {formatBankMoney(remaining)}
                </button>
              ) : (
                <span className="text-lg font-semibold tabular-nums text-gray-900">
                  {formatBankMoney(remaining)}
                </span>
              )}
              {line.allocatedAmount > 0 && (
                <span className="text-gray-500">
                  из {formatBankMoney(line.amount)}, остальное уже разнесено
                </span>
              )}
              <span className="text-gray-600">
                {formatBankDate(line.postedAt)} · {line.counterpartyName}
                {line.counterpartyInn ? ` · ИНН ${line.counterpartyInn}` : ''}
              </span>
            </div>
            <p className="mt-1 text-gray-700">{line.purpose}</p>
          </div>

          {query.isLoading ? (
            <PageLoader label="Ищу клиента и заказы…" />
          ) : query.isError || !data ? (
            <ErrorMessage error={query.error} />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <BankMatchBadge state={data.state} />
                <span className="text-gray-700">{data.reason}</span>
              </div>

              <BankClientPicker
                clients={data.clients}
                clientId={data.clientId}
                onPick={setPickedClientId}
              />

              {data.clientId && data.orders.length === 0 && (
                <p className="text-sm text-gray-500">
                  У клиента нет заказов, ждущих оплаты. Закройте окно — платёж останется в очереди
                  «не разнесено», пока менеджер не заведёт заказ.
                </p>
              )}

              {data.orders.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-left text-xs uppercase text-gray-500">
                      <tr>
                        <th className="py-1 pr-3">Заказ</th>
                        <th className="py-1 pr-3">Счёт</th>
                        <th className="py-1 pr-3 text-right">Осталось оплатить</th>
                        <th className="py-1 text-right">Разнести, ₽</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.orders.map((o) => {
                        const r = rows[o.orderId] ?? emptyRow(o)
                        const twin = o.manualPayments.find((p) => p.id === r.linkPaymentId)
                        return (
                          <tr key={o.orderId}>
                            <td className="py-2 pr-3 align-top">
                              <a
                                href={`/orders/${o.orderId}`}
                                target="_blank"
                                rel="noreferrer"
                                title="Открыть заказ в новой вкладке — окно разноса останется открытым"
                                className="font-medium text-brand-600 hover:text-brand-700"
                              >
                                {o.requestNumber ?? 'заказ'} ↗
                              </a>
                              <div className="text-xs text-gray-500">
                                {ORDER_STATUS_LABELS_RU[o.status as OrderStatus] ?? o.status}
                                {o.managerName ? ` · ${o.managerName}` : ''}
                              </div>
                            </td>
                            <td className="py-2 pr-3 align-top">
                              {o.invoices.length === 0 ? (
                                <span className="text-gray-400">счёт не выставлен</span>
                              ) : (
                                <select
                                  value={r.invoiceId ?? ''}
                                  onChange={(e) =>
                                    setRow(o.orderId, { invoiceId: e.target.value || null })
                                  }
                                  aria-label={`Счёт заказа ${o.requestNumber ?? ''}`}
                                  className="max-w-[220px] rounded-lg border border-gray-300 px-2 py-1 text-sm"
                                >
                                  {o.invoices.length > 1 && <option value="">Без счёта</option>}
                                  {o.invoices.map((i) => (
                                    <option key={i.id} value={i.id}>
                                      {i.invoiceNumber ? `№ ${i.invoiceNumber}` : 'без номера'} ·{' '}
                                      {formatBankMoney(i.totalSum)}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </td>
                            <td className="py-2 pr-3 text-right align-top tabular-nums text-gray-700">
                              {formatBankMoney(o.remaining)}
                            </td>
                            <td className="py-2 text-right align-top">
                              <div className="ml-auto w-36">
                                <MoneyInput
                                  key={`${o.orderId}-${prefill}`}
                                  value={r.amount}
                                  onChange={(v) =>
                                    setRow(o.orderId, { amount: v, linkPaymentId: null })
                                  }
                                />
                              </div>
                              {fillAmount(o) > 0 && fillAmount(o) !== (r.amount ?? 0) && (
                                <button
                                  type="button"
                                  onClick={() => fill(o)}
                                  className="mt-1 text-xs text-brand-600 hover:text-brand-700"
                                >
                                  Вся сумма: {formatBankMoney(fillAmount(o))}
                                </button>
                              )}
                              {twin && (
                                <div className="mt-1 text-xs text-gray-500">
                                  уже внесена вручную {formatBankDate(twin.paidAt)} — строка её
                                  подтвердит, второй оплаты не будет
                                </div>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {hint && hintUsed && (
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={saveNumber}
                    onChange={(e) => setSaveNumber(e.target.checked)}
                    className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  Записать номер счёта {hint.number} в счёт заказа
                </label>
              )}
              {innToSave && (
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={saveInn}
                    onChange={(e) => setSaveInn(e.target.checked)}
                    className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  Сохранить ИНН {line.counterpartyInn} клиенту «{client?.name}»
                </label>
              )}
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3">
          <div className="text-sm">
            <span className="text-gray-600">Разносится: </span>
            <span className="font-medium tabular-nums">{formatBankMoney(total)}</span>
            {rest < -0.005 ? (
              <span className="ml-3 text-red-600">на {formatBankMoney(-rest)} больше платежа</span>
            ) : rest > 0.005 ? (
              <span className="ml-3 text-gray-500">
                останется в очереди: {formatBankMoney(rest)}
              </span>
            ) : null}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={!canSubmit}
              className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-60"
            >
              {saving ? 'Разношу…' : 'Разнести'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
